<template>
  <m-step-layout title="Payment Instructions" class="mt-16" type="sm">
    <template #description>
      <!-- eslint-disable-next-line max-len -->
      <p class="text-sm text-gray-700">
        This shows how the rules will be described in the documentation. Feel
        free to re-order them or add any others you wish to write yourself here.
        If you want to use the rule building wizard to change rules you have
        already added, go back to the previous screen.
      </p>
    </template>

    <m-form>
      <div class="mt-4">
        <p class="font-medium text-gray-800">Rules:</p>
        <div class="overflow-hidden mt-2">
          <div v-for="(rule, idx) in rules" :key="idx">
            <div v-if="typeof rule !== 'string'" class="col-span-12 items-center space-x-2">
              <payment-instruction-view
                :instruction="rule"
                @up="handleUp(idx)"
                @down="handleDown(idx)"
                @delete="handleDelete(idx)"
              ></payment-instruction-view>
            </div>
            <div class="py-2 col-span-12 items-center" v-else>
              <div class="grid grid-cols-12">
                <div class="col-span-1 flex flex-col">
                  <button
                    class="flex justify-center focus:outline-none"
                    @click="handleUp(idx)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                  <button
                    class="flex justify-center focus:outline-none"
                    @click="handleDown(idx)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
                <p class="col-span-10 text-sm pt-1">{{ rule }}</p>
                <div class="col-span-1 flex space-x-2">
                  <button class="focus:outline-none"
                    @click="handleEdit(idx)"
                    v-if="typeof rule === 'string'"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <!-- eslint-disable max-len -->
                      <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                      <path fill-rule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clip-rule="evenodd" />
                      <!-- eslint-enable max-len -->
                    </svg>
                  </button>
                  <button class="focus:outline-none" @click="handleDelete(idx)">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-red-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <!-- eslint-disable-next-line max-len -->
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8">
        <label class="text-sm font-medium text-gray-800">
          Write your own rule:
        </label>
        <m-textarea v-model="text" />
        <p class="text-xs text-gray-600">
          Any rule you write yourself will be added to the documentation exactly
          as you write it. Use the arrows above to re-order any of the rules
          including those you create yourself.
        </p>
        <div class="mt-2 flex justify-end">
          <m-button v-if="isEdit"
            @click="handleSaveRule"
            :disabled="$v.text.$invalid"
            :class="{ 'cursor-not-allowed': $v.text.$invalid }"
          >Update Custom Rule</m-button>
          <m-button v-else
            @click="handleAddRule"
            :disabled="$v.text.$invalid"
            :class="{ 'cursor-not-allowed': $v.text.$invalid }"
          >Add Custom Rule</m-button>
        </div>
      </div>
    </m-form>
    <div class="mt-6 text-right">
      <m-button class="ml-2" @click="handleBackStep" ref="back-btn">
        Back
      </m-button>
      <m-button
        class="ml-2"
        @click="handleNextStep"
        ref="next-btn"
        data-test="btn-next"
        :disabled="this.rules.length === 0"
        :class="{ 'cursor-not-allowed': this.rules.length === 0 }"
        >Continue</m-button
      >
    </div>
  </m-step-layout>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import _ from 'lodash';
import ToolKitMixin from '@/app/mixins/toolkit/item';
import { arrayMove } from '@/app/utils/utils';
import PaymentInstructionView from '../payment-instructions/payment-instruction-view.vue';

export default {
  name: 'payment-instructions-custom',
  mixins: [ToolKitMixin],
  components: {
    PaymentInstructionView,
  },
  data() {
    return {
      isEdit: false,
      editing: 0,
      text: '',
      rules: [],
    };
  },
  computed: {
    members() {
      const { members } = this.getItem;
      const formattedMembers = _.values(members);

      return formattedMembers;
    },
  },
  methods: {
    handleAddRule() {
      this.rules.push(this.text);
      this.text = '';
    },
    handleEdit(idx) {
      this.text = this.rules[idx];
      this.editing = idx;
      this.isEdit = true;
    },
    handleSaveRule() {
      this.rules.splice(this.editing, 1, this.text);
      this.text = '';
      this.editing = 0;
      this.isEdit = false;
    },
    handleDelete(num) {
      const { rules } = this;
      const filtered = rules.filter((rule, idx) => idx !== num);
      this.rules = [...filtered];
    },
    handleUp(idx) {
      if (idx === 0) return;
      this.rules = arrayMove(this.rules, idx, idx - 1);
    },
    handleDown(idx) {
      if (idx + 1 === this.rules.length) return;
      this.rules = arrayMove(this.rules, idx, idx + 1);
    },
    async handleBackStep() {
      await this.setItem({ rules: this.rules, replace: true });
      this.$emit('backStep');
    },
    async handleNextStep() {
      await this.setItem({ rules: this.rules, replace: true });
      this.$emit('nextStep');
    },
  },
  activated() {
    this.rules = this.getItem.rules;
  },
  validations: {
    text: {
      required,
    },
  },
};
</script>
